'use client';
import { useTranslation } from '../../lib/i18n';
import type { PluginDockItem } from '../../lib/operationDockPlugins';
import { Button, buttonClassName } from './Button';
import { Modal, ModalBody, ModalFooter } from './Modal';
import { Progress } from './shadcn/progress';
import { Spinner } from './states';
import { useToast } from './Toast';

/** Shared presentation of a core or plugin durable operation. Meaning, result and link come from its source. */
export function PluginOperationProgressDialog({ operation, onClose, onClosed, onHistory }: {
  operation: Pick<PluginDockItem, 'title' | 'status' | 'statusLabel' | 'detail' | 'percent' | 'historyUrl'>; onClose: () => void; onClosed: () => void; onHistory: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const running = operation.status === 'pending' || operation.status === 'running';
  const failed = operation.status === 'failed';
  return <Modal title={operation.title} size="sm" presentation="center" onClose={onClose} onClosed={onClosed}
    aria-busy={running || undefined} data-testid="plugin-operation-progress-dialog">
    <ModalBody gap={4}>
      <div className="flex items-center gap-2 text-ui">
        {running ? <Spinner size="sm" /> : null}
        <span role={failed ? 'alert' : 'status'} className={failed ? 'text-destructive' : undefined}>{operation.statusLabel}</span>
      </div>
      {operation.detail ? <p className="whitespace-pre-wrap break-words text-ui">{operation.detail}</p> : null}
      {running ? <Progress aria-label={t.operationProgress.progressLabel} value={operation.percent}
        indicatorValue={operation.percent ?? 100} indicatorClassName={operation.percent === null ? 'operation-progress-indeterminate' : undefined} /> : null}
    </ModalBody>
    <ModalFooter>
      <a href={operation.historyUrl} className={buttonClassName('outline')} onClick={event => { event.preventDefault(); void onHistory().catch(error => toast(t.operationDock.ackFailed, 'error', { error })); }}>{t.operationDock.history}</a>
      <Button variant="ghost" onClick={onClose}>{running ? t.operationProgress.minimize : t.common.close}</Button>
    </ModalFooter>
  </Modal>;
}
